import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BsCreditCard2Back, BsExclamationTriangleFill, BsArrowCounterclockwise } from "react-icons/bs";
import Sidebar from "../Sidebar";
import Header from "../Header";
import API_BASE_URL from "../../config";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import { notify, confirmDialog } from "../../components/Notify";
import "./tpl.css";
import "./Billing.css";

const moneyFormatter = (amount, currency = "USD") => {
  if (amount === null || amount === undefined) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(Number(amount));
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "--";
  }
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(date);
};

const normalizeOverview = (payload) => {
  if (!payload) return null;
  const usage = payload.usage
    ? {
        smsUsed: payload.usage.sms_used ?? payload.usage.smsUsed ?? 0,
        devicesLinked: payload.usage.devices_linked ?? payload.usage.devicesLinked ?? 0,
      }
    : null;

  const invoices = Array.isArray(payload.invoices)
    ? payload.invoices.map((invoice) => ({
        id: invoice.id,
        stripeId: invoice.stripe_invoice_id ?? invoice.stripeInvoiceId,
        status: invoice.status,
        amountDue: invoice.amount_due ?? invoice.amountDue,
        currency: (invoice.currency || "usd").toUpperCase(),
        hostedUrl: invoice.hosted_invoice_url ?? invoice.hostedInvoiceUrl,
        pdfUrl: invoice.pdf_url ?? invoice.pdfUrl,
        periodStart: invoice.period_start ?? invoice.periodStart,
        periodEnd: invoice.period_end ?? invoice.periodEnd,
      }))
    : [];

  return { ...payload, usage, invoices };
};

export default function Billing() {
  const [overview, setOverview] = useState(null);
  const [channelData, setChannelData] = useState({ channels: [], invoices: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const navigate = useNavigate();
  const isAdminUser = (localStorage.getItem("userRole") || "").toLowerCase() === "admin";

  const showToast = (message, type = "success") => notify(message, type);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [{ data }, ch] = await Promise.all([
        axios.get(`${API_BASE_URL}/subscription`, { headers }),
        axios.get(`${API_BASE_URL}/services/billing`, { headers }).catch(() => ({ data: { channels: [], invoices: [] } })),
      ]);
      setOverview(normalizeOverview(data));
      setChannelData(ch.data || { channels: [], invoices: [] });
    } catch (err) {
      console.error("Billing:overview error", err);
      setError(err.response?.data?.error || err.message || "Unable to load billing data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handlePortal = async () => {
    try {
      setPortalLoading(true);
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.post(`${API_BASE_URL}/billing/portal-session`, {}, { headers });
      if (data?.url) {
        try {
          const parsed = new URL(data.url);
          if (!["billing.stripe.com", "checkout.stripe.com"].includes(parsed.hostname)) {
            throw new Error("Unexpected billing URL");
          }
          window.open(data.url, "_blank", "noopener");
        } catch { throw new Error("Invalid billing URL."); }
      } else {
        throw new Error("Billing portal link unavailable.");
      }
    } catch (err) {
      showToast(err.response?.data?.error || err.message, "error");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelSuccess = async (data) => {
    showToast(data?.message || "Subscription cancelled. You can keep using Pairfon until the end of the period.");
    setCancelModalOpen(false);
    await fetchOverview();
  };

  const handleReactivate = async () => {
    try {
      setReactivateLoading(true);
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.post(`${API_BASE_URL}/billing/reactivate-subscription`, {}, { headers });
      showToast(data?.message || "Subscription reactivated.");
      await fetchOverview();
    } catch (err) {
      showToast(err.response?.data?.error || err.message, "error");
    } finally {
      setReactivateLoading(false);
    }
  };

  const refreshUsage = async () => {
    try {
      setUsageLoading(true);
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_BASE_URL}/subscription/usage/recalculate`, {}, { headers });
      await fetchOverview();
      showToast("Usage refreshed successfully");
    } catch (err) {
      showToast(err.response?.data?.error || err.message, "error");
    } finally {
      setUsageLoading(false);
    }
  };

  const cancelChannel = async (service) => {
    const ok = await confirmDialog({
      title: `Cancel ${labelOf(service)}?`,
      message: `Its numbers will be disconnected and billing stops immediately.`,
      danger: true,
      okText: "Cancel subscription",
      cancelText: "Keep it",
    });
    if (!ok) return;
    try {
      const token = localStorage.getItem("userToken");
      await axios.post(`${API_BASE_URL}/services/${service}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showToast(`${service} subscription cancelled.`);
      await fetchOverview();
    } catch (err) {
      showToast(err.response?.data?.error || err.message, "error");
    }
  };

  const plan = overview?.plan;
  const subscription = overview?.subscription;
  const usage = overview?.usage;
  const invoices = overview?.invoices || [];
  const channels = (channelData?.channels || []).filter((c) => c.status === "active" || c.quantity > 0);
  const labelOf = (s) => (s === "whatsapp" ? "WhatsApp" : s === "imessage" ? "iMessage" : s);
  // Prefer the complete Stripe invoice list (all charges); fall back to SMS-only.
  const displayInvoices = (channelData?.invoices && channelData.invoices.length)
    ? channelData.invoices.map((i) => ({ id: i.id, label: i.number || i.id, desc: i.description, amount: i.total, currency: i.currency, status: i.status, when: i.created, hostedUrl: i.hostedUrl, pdfUrl: i.pdfUrl }))
    : invoices.map((i) => ({ id: i.id, label: i.stripeId || i.id, desc: null, amount: i.amountDue, currency: i.currency, status: i.status, when: i.periodStart, hostedUrl: i.hostedUrl, pdfUrl: i.pdfUrl }));
  const deviceLimit = plan?.devicesAllowed || 0;
  const devicesLinked = usage?.devicesLinked || 0;
  const devicePercent = deviceLimit ? Math.min(100, Math.round((devicesLinked / deviceLimit) * 100)) : 0;
  const smsUsed = usage?.smsUsed || 0;
  const statusText = (subscription?.status || "inactive").replace(/_/g, " ");
  const isActive = subscription?.status === "active" || subscription?.status === "trialing";
  const cancelPending = subscription?.cancelAtPeriodEnd === true || subscription?.autoRenew === false;
  const canCancel = isAdminUser && isActive && !cancelPending && Boolean(subscription?.stripeId);
  const canReactivate = isAdminUser && cancelPending && Boolean(subscription?.stripeId);

  const invoiceTag = (s) => (s === "paid" ? "ok" : s === "open" || s === "draft" ? "pend" : "off");

  return (
    <div className="page-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4 bill-wrap">
          <div className="page-header dash-head">
            <div>
              <h4>Billing</h4>
              <p className="page-subtitle">Your plan, usage and invoices.</p>
            </div>
            {!loading && overview && (
              <button className="tpl-btn ghost" style={{ flex: "0 0 auto" }} onClick={refreshUsage} disabled={usageLoading}>
                {usageLoading ? "Refreshing…" : "Refresh usage"}
              </button>
            )}
          </div>

          {error && (
            <div className="billing-error-card">
              <BsExclamationTriangleFill />
              <span>{error}</span>
              <button className="btn-save" onClick={fetchOverview}>Retry</button>
            </div>
          )}

          {loading ? (
            <div className="page-loading"><div className="spinner" /><p>Loading billing data…</p></div>
          ) : (
            overview && (
              <>
                {cancelPending && subscription?.stripeId && (
                  <div className="billing-cancel-banner">
                    <BsExclamationTriangleFill className="billing-cancel-banner__icon" />
                    <div className="billing-cancel-banner__body">
                      <strong>Your subscription is set to end on {formatDate(subscription?.currentPeriodEnd)}.</strong>
                      <span>You'll lose access after that. Reactivate anytime before then to keep your service.</span>
                    </div>
                    {canReactivate && (
                      <button type="button" className="billing-cancel-banner__btn" onClick={handleReactivate} disabled={reactivateLoading}>
                        <BsArrowCounterclockwise />
                        {reactivateLoading ? "Reactivating…" : "Reactivate"}
                      </button>
                    )}
                  </div>
                )}

                <div className="tpl-grid2">
                  {/* Current plan */}
                  <div className="tpl-card">
                    <div className="tpl-card-h"><h6>Current plan</h6></div>
                    <div className="bill-plan-top">
                      <span className="bill-plan-name">{plan?.name ? plan.name : "No plan"}</span>
                      <span className="tpl-mut">
                        {plan?.price !== undefined ? `${moneyFormatter(plan.price)} / ${plan?.billingCycle || "month"}` : "SMS · Android"}
                      </span>
                      <span className={`tpl-tag ${isActive ? "ok" : "off"}`} style={{ marginLeft: "auto", textTransform: "capitalize" }}>{statusText}</span>
                    </div>

                    <div className="tpl-usage">
                      <div className="tpl-usage-top"><span className="tpl-mut">Devices</span><span>{devicesLinked} / {deviceLimit || "∞"}</span></div>
                      <div className="tpl-bar"><i style={{ width: `${deviceLimit ? devicePercent : 0}%` }} /></div>
                    </div>
                    <div className="tpl-usage">
                      <div className="tpl-usage-top"><span className="tpl-mut">Messages this period</span><span>{smsUsed.toLocaleString()}</span></div>
                    </div>

                    <div className="tpl-btnrow" style={{ marginTop: 16 }}>
                      <button className="tpl-btn" onClick={() => navigate("/plans")}>Upgrade</button>
                      {canCancel && <button className="tpl-btn ghost" onClick={() => setCancelModalOpen(true)}>Cancel plan</button>}
                      {canReactivate && (
                        <button className="tpl-btn ghost" onClick={handleReactivate} disabled={reactivateLoading}>
                          {reactivateLoading ? "Reactivating…" : "Reactivate"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Payment & billing */}
                  <div className="tpl-card">
                    <div className="tpl-card-h"><h6>Payment method</h6></div>
                    <div className="tpl-row" style={{ borderBottom: 0, paddingTop: 2 }}>
                      <div className="ci" style={{ background: "rgba(124,124,255,.16)", color: "#c7c9ff" }}><BsCreditCard2Back /></div>
                      <div>
                        <div className="nm">Managed by Stripe</div>
                        <div className="sub">Cards, invoices &amp; receipts</div>
                      </div>
                      <button className="tpl-lnk" style={{ marginLeft: "auto" }} onClick={handlePortal} disabled={portalLoading}>
                        {portalLoading ? "Opening…" : "Open portal"}
                      </button>
                    </div>
                    {subscription?.currentPeriodEnd && (
                      <p className="tpl-mut" style={{ fontSize: "0.82rem", margin: "10px 0 0" }}>
                        {cancelPending ? "Access ends " : "Renews "}
                        <b style={{ color: "var(--pf-text)" }}>{formatDate(subscription.currentPeriodEnd)}</b>.
                      </p>
                    )}
                    <div className="tpl-btnrow" style={{ marginTop: 14 }}>
                      <button className="tpl-btn ghost" onClick={() => navigate("/plans")}>View plans</button>
                    </div>
                  </div>
                </div>

                {/* Channel subscriptions (per-number, billed separately) */}
                {channels.length > 0 && (
                  <div className="tpl-card">
                    <div className="tpl-card-h"><h6>Channel subscriptions</h6></div>
                    {channels.map((c) => (
                      <div className="tpl-row" key={c.service}>
                        <span className="bill-dot" style={{ background: c.service === "whatsapp" ? "#25d366" : "#0d6efd" }} />
                        <div>
                          <div className="nm">{labelOf(c.service)}</div>
                          <div className="sub">{c.quantity} number{c.quantity !== 1 ? "s" : ""} × ${c.unit} · <span style={{ textTransform: "capitalize" }}>{c.status}</span></div>
                        </div>
                        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
                          <span className="bill-price">${c.monthly}<small>/mo</small></span>
                          {isAdminUser && c.status === "active" && c.stripeSubscriptionId && (
                            <button className="tpl-lnk danger" onClick={() => cancelChannel(c.service)}>Cancel</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Invoices */}
                <div className="tpl-card">
                  <div className="tpl-card-h">
                    <h6>Invoices</h6>
                    <button className="tpl-lnk" onClick={handlePortal} disabled={portalLoading}>Billing portal →</button>
                  </div>
                  {displayInvoices.length === 0 ? (
                    <p className="tpl-mut" style={{ margin: 0 }}>Invoices will appear once billing starts.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="tpl-table">
                        <thead><tr><th>Date</th><th>Amount</th><th>Plan</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                          {displayInvoices.map((invoice) => (
                            <tr key={invoice.id}>
                              <td>{formatDate(invoice.when)}</td>
                              <td style={{ fontWeight: 600 }}>{moneyFormatter(invoice.amount, invoice.currency)}</td>
                              <td className="tpl-mut">{invoice.desc || plan?.name || "—"}</td>
                              <td><span className={`tpl-tag ${invoiceTag(invoice.status)}`} style={{ textTransform: "capitalize" }}>{invoice.status || "—"}</span></td>
                              <td className="tpl-actions">
                                {(invoice.pdfUrl || invoice.hostedUrl) ? (
                                  <a className="tpl-lnk" href={invoice.pdfUrl || invoice.hostedUrl} target="_blank" rel="noopener noreferrer">PDF</a>
                                ) : (
                                  <span className="tpl-mut">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )
          )}
        </div>
      </div>

      <CancelSubscriptionModal
        open={cancelModalOpen}
        periodEnd={subscription?.currentPeriodEnd}
        onClose={() => setCancelModalOpen(false)}
        onCancelled={handleCancelSuccess}
      />
    </div>
  );
}
